import type { ModifierOverride } from "../app/storeTypes.ts";
import type {
  BlendBevelModifier,
  BlendModifier,
} from "../blend-reader/types/BlendModifier.ts";
import {
  edgeFaces,
  getAdjacencyCache,
  triangulateTopologyForRendering,
} from "../modeling/topology.ts";
import {
  asLoopNormals,
  type PolygonTopology,
  type SceneObject,
} from "../modeling/types.ts";
import { applyModifiersCore } from "../viewport/modifierPipeline.ts";
import {
  type BevelEdgeDiagnostic,
  diagnoseBevelEdges,
} from "../viewport/modifiers/bevelSelection.ts";
import {
  buildSceneObjectModifierGeometry,
  buildSceneObjectNameMap,
  mergeModifierStack,
  resolveModifierEvaluation,
} from "../viewport/modifiers/evaluation.ts";
import { projectVertexGroupThroughProvenance } from "../viewport/modifiers/helpers.ts";
import type { ModifierGeometry } from "../viewport/modifiers/modifierTypes.ts";
import { serializeMeshDebug } from "./meshDebugExport.ts";

export interface GeometrySummary {
  vertexCount: number;
  faceCount: number;
  edgeCount: number;
  triangleIndexCount: number;
  triCount: number;
  quadCount: number;
  ngonCount: number;
  invalidFaces: Array<{ faceIndex: number; vertices: number[] }>;
}

export interface BevelSelectionSummary {
  selectedEdgeCount: number;
  convexSelected: number;
  concaveSelected: number;
  unknownSelected: number;
  rejectedInteriorEdges: number;
  touchedFaceCount: number;
  reasonCounts: Partial<Record<BevelEdgeDiagnostic["reason"], number>>;
}

export interface BevelStepSnapshot {
  stepNumber: number;
  modifierIndex: number;
  modifierLabel: string;
  settingsLine: string;
  inputSummary: GeometrySummary;
  outputSummary: GeometrySummary;
  stage1Summary: GeometrySummary | null;
  selectionSummary: BevelSelectionSummary | null;
  outputDiagnostics: OutputGeometryDiagnostics | null;
}

export interface BevelDebugSnapshot {
  objectId: string;
  objectName: string;
  modifierCount: number;
  bevelModifierCount: number;
  steps: BevelStepSnapshot[];
}

export interface OutputFaceDiagnostic {
  faceIndex: number;
  vertices: number[];
  issue: "twistedQuad" | "nonPlanarQuad" | "degenerateFace";
  foldAngleDegrees: number | null;
  area: number;
  source: string | null;
  owner: string | null;
}

export interface OutputEdgePairDiagnostic {
  edgeIndex: number;
  edge: [number, number];
  faces: [number, number];
  faceDot: number;
  faceAngleDegrees: number;
  source: string | null;
  owner: string | null;
}

export interface OutputGeometryDiagnostics {
  twistedQuadCount: number;
  nonPlanarQuadCount: number;
  degenerateFaceCount: number;
  flippedAdjacentPairCount: number;
  suspectFaceSourceCounts: Record<string, number>;
  suspectFaceOwnerCounts: Record<string, number>;
  flippedPairSourceCounts: Record<string, number>;
  flippedPairOwnerCounts: Record<string, number>;
  suspectFaces: OutputFaceDiagnostic[];
  flippedAdjacentPairs: OutputEdgePairDiagnostic[];
}

const NON_PLANAR_QUAD_ANGLE_DEGREES = 15;
const FLIPPED_ADJACENT_DOT_THRESHOLD = -0.25;
const DEGENERATE_FACE_AREA_EPSILON = 1e-8;

const FACE_SOURCE_LABELS = [
  "Stage1",
  "StructuredBaseGrid",
  "ExpandedBaseBoundary",
  "CornerPatchFallback",
  "CornerPatchQuadRing",
  "CornerPatchCore",
  "StripAcross01",
  "StripAcross03",
] as const;

function faceSourceLabel(
  faceSources: Uint8Array | undefined,
  faceIndex: number,
): string | null {
  if (!faceSources || faceIndex < 0 || faceIndex >= faceSources.length)
    return null;
  const code = faceSources[faceIndex]!;
  return FACE_SOURCE_LABELS[code] ?? `Unknown(${code})`;
}

function pairSourceLabel(
  faceSources: Uint8Array | undefined,
  faceA: number,
  faceB: number,
): string | null {
  const sourceA = faceSourceLabel(faceSources, faceA);
  const sourceB = faceSourceLabel(faceSources, faceB);
  if (!sourceA && !sourceB) return null;
  if (sourceA === sourceB) return sourceA;
  return `${sourceA ?? "unknown"}/${sourceB ?? "unknown"}`;
}

function incrementCount(
  counts: Record<string, number>,
  key: string | null,
): void {
  if (!key) return;
  counts[key] = (counts[key] ?? 0) + 1;
}

function faceOwnerLabel(
  faceOwnerVertices: Uint32Array | undefined,
  faceIndex: number,
): string | null {
  if (
    !faceOwnerVertices ||
    faceIndex < 0 ||
    faceIndex >= faceOwnerVertices.length
  )
    return null;
  const owner = faceOwnerVertices[faceIndex]!;
  return owner === 0xffff_ffff ? null : `v${owner}`;
}

function pairOwnerLabel(
  faceOwnerVertices: Uint32Array | undefined,
  faceA: number,
  faceB: number,
): string | null {
  const ownerA = faceOwnerLabel(faceOwnerVertices, faceA);
  const ownerB = faceOwnerLabel(faceOwnerVertices, faceB);
  if (!ownerA && !ownerB) return null;
  if (ownerA === ownerB) return ownerA;
  return `${ownerA ?? "none"}/${ownerB ?? "none"}`;
}

function fmt(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "n/a";
  return value.toFixed(6);
}

function accumulateFaceNormal(
  topology: PolygonTopology,
  positions: Float32Array,
  faceIndex: number,
): [number, number, number] {
  const start = topology.offsets[faceIndex]!;
  const end = topology.offsets[faceIndex + 1]!;
  const size = end - start;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let j = 0; j < size; j++) {
    const a = topology.corners[start + j]!;
    const b = topology.corners[start + ((j + 1) % size)]!;
    const ax = positions[a * 3]!;
    const ay = positions[a * 3 + 1]!;
    const az = positions[a * 3 + 2]!;
    const bx = positions[b * 3]!;
    const by = positions[b * 3 + 1]!;
    const bz = positions[b * 3 + 2]!;
    nx += (ay - by) * (az + bz);
    ny += (az - bz) * (ax + bx);
    nz += (ax - bx) * (ay + by);
  }
  const len = Math.hypot(nx, ny, nz);
  if (len <= 1e-8) return [0, 0, 0];
  return [nx / len, ny / len, nz / len];
}

function triangleNormal(
  positions: Float32Array,
  a: number,
  b: number,
  c: number,
): [number, number, number] {
  const ax = positions[a * 3]!;
  const ay = positions[a * 3 + 1]!;
  const az = positions[a * 3 + 2]!;
  const bx = positions[b * 3]!;
  const by = positions[b * 3 + 1]!;
  const bz = positions[b * 3 + 2]!;
  const cx = positions[c * 3]!;
  const cy = positions[c * 3 + 1]!;
  const cz = positions[c * 3 + 2]!;

  const abx = bx - ax;
  const aby = by - ay;
  const abz = bz - az;
  const acx = cx - ax;
  const acy = cy - ay;
  const acz = cz - az;

  return [aby * acz - abz * acy, abz * acx - abx * acz, abx * acy - aby * acx];
}

function normalizedDot(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number | null {
  const al = Math.hypot(a[0], a[1], a[2]);
  const bl = Math.hypot(b[0], b[1], b[2]);
  if (al <= 1e-8 || bl <= 1e-8) return null;
  return (a[0] * b[0] + a[1] * b[1] + a[2] * b[2]) / (al * bl);
}

function faceArea(
  topology: PolygonTopology,
  positions: Float32Array,
  faceIndex: number,
): number {
  const start = topology.offsets[faceIndex]!;
  const end = topology.offsets[faceIndex + 1]!;
  const size = end - start;
  if (size < 3) return 0;
  const v0 = topology.corners[start]!;
  let area = 0;
  for (let j = 1; j < size - 1; j++) {
    const n = triangleNormal(
      positions,
      v0,
      topology.corners[start + j]!,
      topology.corners[start + j + 1]!,
    );
    area += 0.5 * Math.hypot(n[0], n[1], n[2]);
  }
  return area;
}

function computeOutputGeometryDiagnostics(
  geo: ModifierGeometry,
): OutputGeometryDiagnostics | null {
  if (!geo.topology) return null;

  const topology = geo.topology;
  const faceCount = topology.offsets.length - 1;
  const faceNormals = new Float32Array(faceCount * 3);
  const suspectFaces: OutputFaceDiagnostic[] = [];
  const flippedAdjacentPairs: OutputEdgePairDiagnostic[] = [];
  const suspectFaceSourceCounts: Record<string, number> = {};
  const suspectFaceOwnerCounts: Record<string, number> = {};
  const flippedPairSourceCounts: Record<string, number> = {};
  const flippedPairOwnerCounts: Record<string, number> = {};
  let twistedQuadCount = 0;
  let nonPlanarQuadCount = 0;
  let degenerateFaceCount = 0;

  for (let faceIndex = 0; faceIndex < faceCount; faceIndex++) {
    const normal = accumulateFaceNormal(topology, geo.positions, faceIndex);
    faceNormals[faceIndex * 3] = normal[0];
    faceNormals[faceIndex * 3 + 1] = normal[1];
    faceNormals[faceIndex * 3 + 2] = normal[2];

    const start = topology.offsets[faceIndex]!;
    const end = topology.offsets[faceIndex + 1]!;
    const vertices = Array.from(topology.corners.slice(start, end));
    const area = faceArea(topology, geo.positions, faceIndex);

    if (area <= DEGENERATE_FACE_AREA_EPSILON) {
      degenerateFaceCount++;
      suspectFaces.push({
        faceIndex,
        vertices,
        issue: "degenerateFace",
        foldAngleDegrees: null,
        area,
        source: faceSourceLabel(geo.faceSources, faceIndex),
        owner: faceOwnerLabel(geo.faceOwnerVertices, faceIndex),
      });
      incrementCount(
        suspectFaceSourceCounts,
        faceSourceLabel(geo.faceSources, faceIndex),
      );
      incrementCount(
        suspectFaceOwnerCounts,
        faceOwnerLabel(geo.faceOwnerVertices, faceIndex),
      );
      continue;
    }

    if (vertices.length === 4) {
      const triA = triangleNormal(
        geo.positions,
        vertices[0]!,
        vertices[1]!,
        vertices[2]!,
      );
      const triB = triangleNormal(
        geo.positions,
        vertices[0]!,
        vertices[2]!,
        vertices[3]!,
      );
      const triDot = normalizedDot(triA, triB);
      if (triDot != null) {
        const foldAngleDegrees =
          (Math.acos(Math.max(-1, Math.min(1, triDot))) * 180) / Math.PI;
        if (triDot < 0) {
          twistedQuadCount++;
          suspectFaces.push({
            faceIndex,
            vertices,
            issue: "twistedQuad",
            foldAngleDegrees,
            area,
            source: faceSourceLabel(geo.faceSources, faceIndex),
            owner: faceOwnerLabel(geo.faceOwnerVertices, faceIndex),
          });
          incrementCount(
            suspectFaceSourceCounts,
            faceSourceLabel(geo.faceSources, faceIndex),
          );
          incrementCount(
            suspectFaceOwnerCounts,
            faceOwnerLabel(geo.faceOwnerVertices, faceIndex),
          );
        } else if (foldAngleDegrees > NON_PLANAR_QUAD_ANGLE_DEGREES) {
          nonPlanarQuadCount++;
          suspectFaces.push({
            faceIndex,
            vertices,
            issue: "nonPlanarQuad",
            foldAngleDegrees,
            area,
            source: faceSourceLabel(geo.faceSources, faceIndex),
            owner: faceOwnerLabel(geo.faceOwnerVertices, faceIndex),
          });
          incrementCount(
            suspectFaceSourceCounts,
            faceSourceLabel(geo.faceSources, faceIndex),
          );
          incrementCount(
            suspectFaceOwnerCounts,
            faceOwnerLabel(geo.faceOwnerVertices, faceIndex),
          );
        }
      }
    }
  }

  const adj = getAdjacencyCache(topology, geo.positions.length / 3);
  const edgeCount = topology.edges.length / 2;
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const faces = edgeFaces(adj, edgeIndex);
    if (faces.length !== 2) continue;
    const a: [number, number, number] = [
      faceNormals[faces[0]! * 3]!,
      faceNormals[faces[0]! * 3 + 1]!,
      faceNormals[faces[0]! * 3 + 2]!,
    ];
    const b: [number, number, number] = [
      faceNormals[faces[1]! * 3]!,
      faceNormals[faces[1]! * 3 + 1]!,
      faceNormals[faces[1]! * 3 + 2]!,
    ];
    const faceDot = normalizedDot(a, b);
    if (faceDot == null || faceDot > FLIPPED_ADJACENT_DOT_THRESHOLD) continue;
    flippedAdjacentPairs.push({
      edgeIndex,
      edge: [
        topology.edges[edgeIndex * 2]!,
        topology.edges[edgeIndex * 2 + 1]!,
      ],
      faces: [faces[0]!, faces[1]!],
      faceDot,
      faceAngleDegrees:
        (Math.acos(Math.max(-1, Math.min(1, faceDot))) * 180) / Math.PI,
      source: pairSourceLabel(geo.faceSources, faces[0]!, faces[1]!),
      owner: pairOwnerLabel(geo.faceOwnerVertices, faces[0]!, faces[1]!),
    });
    incrementCount(
      flippedPairSourceCounts,
      pairSourceLabel(geo.faceSources, faces[0]!, faces[1]!),
    );
    incrementCount(
      flippedPairOwnerCounts,
      pairOwnerLabel(geo.faceOwnerVertices, faces[0]!, faces[1]!),
    );
  }

  suspectFaces.sort((a, b) => {
    const af = a.foldAngleDegrees ?? -1;
    const bf = b.foldAngleDegrees ?? -1;
    return bf - af;
  });
  flippedAdjacentPairs.sort((a, b) => a.faceDot - b.faceDot);

  return {
    twistedQuadCount,
    nonPlanarQuadCount,
    degenerateFaceCount,
    flippedAdjacentPairCount: flippedAdjacentPairs.length,
    suspectFaceSourceCounts,
    suspectFaceOwnerCounts,
    flippedPairSourceCounts,
    flippedPairOwnerCounts,
    suspectFaces: suspectFaces.slice(0, 16),
    flippedAdjacentPairs: flippedAdjacentPairs.slice(0, 16),
  };
}

function summarizeGeometry(geo: ModifierGeometry): GeometrySummary {
  const topology = geo.topology;
  const faceCount = topology ? topology.offsets.length - 1 : 0;
  const invalidFaces: Array<{ faceIndex: number; vertices: number[] }> = [];
  let triCount = 0;
  let quadCount = 0;
  let ngonCount = 0;

  if (topology) {
    for (let f = 0; f < faceCount; f++) {
      const start = topology.offsets[f]!;
      const end = topology.offsets[f + 1]!;
      const vertices = Array.from(topology.corners.slice(start, end));
      if (vertices.length === 3) triCount++;
      else if (vertices.length === 4) quadCount++;
      else if (vertices.length > 4) ngonCount++;
      if (new Set(vertices).size !== vertices.length) {
        invalidFaces.push({ faceIndex: f, vertices });
      }
    }
  }

  return {
    vertexCount: geo.positions.length / 3,
    faceCount,
    edgeCount: topology ? topology.edges.length / 2 : 0,
    triangleIndexCount: geo.indices.length,
    triCount,
    quadCount,
    ngonCount,
    invalidFaces,
  };
}

function debugSceneObjectFromGeometry(
  source: SceneObject,
  geo: ModifierGeometry,
): SceneObject | null {
  if (!geo.topology) return null;
  return {
    ...source,
    vertices: new Float32Array(geo.positions),
    indices: triangulateTopologyForRendering(geo.topology),
    topology: {
      offsets: new Uint32Array(geo.topology.offsets),
      corners: new Uint32Array(geo.topology.corners),
      edges: new Uint32Array(geo.topology.edges),
      cornerEdges: new Uint32Array(geo.topology.cornerEdges),
      edgeFlags: new Uint8Array(geo.topology.edgeFlags),
    },
    loopNormals: asLoopNormals(
      new Float32Array(geo.topology.corners.length * 3),
    ),
    uvs: undefined,
  };
}

function modifierLabel(mod: BlendModifier, index: number): string {
  return `m${index} ${mod.enabled ? "[on]" : "[off]"} ${mod.kind} "${mod.name}"`;
}

function bevelSettingsLine(mod: BlendBevelModifier): string {
  return [
    `limit=${mod.limitMethod}`,
    `affect=${mod.affect}`,
    `offset=${fmt(mod.offset)}`,
    `segments=${mod.segments | 0}`,
    `profile=${fmt(mod.profile)}`,
    `angleLimit=${fmt(mod.angleLimit)}`,
    `clampOverlap=${mod.clampOverlap ? 1 : 0}`,
    `markSharp=${mod.markSharp ? 1 : 0}`,
    `markSeam=${mod.markSeam ? 1 : 0}`,
    `vertexGroupId=${mod.vertexGroupId ?? "none"}`,
    `resolvedVertexGroupSize=${mod.vertexGroupIndices?.length ?? 0}`,
  ].join(" ");
}

function edgeDiagnosticLine(diag: BevelEdgeDiagnostic): string {
  const faces = diag.faces.length > 0 ? diag.faces.join(" ") : "none";
  const sign = diag.profileSign == null ? "n/a" : diag.profileSign.toFixed(1);
  return `e${diag.edgeIndex} [${diag.reason}${diag.selected ? ` ${diag.profileKind}` : ""}]: ${diag.edge[0]} ${diag.edge[1]} faces=${faces} dot=${fmt(diag.faceDot)} angleDeg=${fmt(diag.faceAngleDegrees)} sign=${sign}`;
}

function summarizeBevelSelection(
  diagnostics: BevelEdgeDiagnostic[],
): BevelSelectionSummary {
  const selected = diagnostics.filter((diag) => diag.selected);
  const interestingRejected = diagnostics.filter(
    (diag) =>
      !diag.selected && diag.reason !== "wire" && diag.reason !== "boundary",
  );
  const touchedFaces = new Set<number>();
  const reasonCounts: Partial<Record<BevelEdgeDiagnostic["reason"], number>> =
    {};
  let convexSelected = 0;
  let concaveSelected = 0;
  let unknownSelected = 0;

  for (const diag of diagnostics) {
    reasonCounts[diag.reason] = (reasonCounts[diag.reason] ?? 0) + 1;
  }

  for (const diag of selected) {
    for (const faceIndex of diag.faces) touchedFaces.add(faceIndex);
    if (diag.profileKind === "convex") convexSelected++;
    else if (diag.profileKind === "concave") concaveSelected++;
    else unknownSelected++;
  }

  return {
    selectedEdgeCount: selected.length,
    convexSelected,
    concaveSelected,
    unknownSelected,
    rejectedInteriorEdges: interestingRejected.length,
    touchedFaceCount: touchedFaces.size,
    reasonCounts,
  };
}

function appendGeometrySummary(
  lines: string[],
  label: string,
  geo: ModifierGeometry,
): void {
  const summary = summarizeGeometry(geo);
  lines.push(`--- ${label} ---`);
  lines.push(`vertexCount: ${summary.vertexCount}`);
  lines.push(`faceCount: ${summary.faceCount}`);
  lines.push(`edgeCount: ${summary.edgeCount}`);
  lines.push(`triangleIndexCount: ${summary.triangleIndexCount}`);
  lines.push(
    `faceKinds: tri=${summary.triCount} quad=${summary.quadCount} ngon=${summary.ngonCount}`,
  );
  if (summary.invalidFaces.length === 0) {
    lines.push("invalidFaces: none");
  } else {
    lines.push(`invalidFaces: ${summary.invalidFaces.length}`);
    for (const invalid of summary.invalidFaces.slice(0, 16)) {
      lines.push(`badFace${invalid.faceIndex}: ${invalid.vertices.join(" ")}`);
    }
  }
  lines.push("");
}

function appendCompactMeshDump(
  lines: string[],
  source: SceneObject,
  geo: ModifierGeometry,
  title: string,
): void {
  const summary = summarizeGeometry(geo);
  if (summary.faceCount > 64 || summary.vertexCount > 128) return;
  const debugObject = debugSceneObjectFromGeometry(source, geo);
  if (!debugObject) return;
  lines.push(serializeMeshDebug(debugObject, { title }));
  lines.push("");
}

function appendOutputDiagnostics(
  lines: string[],
  diagnostics: OutputGeometryDiagnostics | null,
): void {
  if (!diagnostics) return;
  lines.push("--- Output Geometry Diagnostics ---");
  lines.push(`twistedQuads: ${diagnostics.twistedQuadCount}`);
  lines.push(`nonPlanarQuads: ${diagnostics.nonPlanarQuadCount}`);
  lines.push(`degenerateFaces: ${diagnostics.degenerateFaceCount}`);
  lines.push(`flippedAdjacentPairs: ${diagnostics.flippedAdjacentPairCount}`);
  if (Object.keys(diagnostics.suspectFaceSourceCounts).length > 0) {
    lines.push(
      `suspectFaceSources: ${Object.entries(diagnostics.suspectFaceSourceCounts)
        .map(([source, count]) => `${source}=${count}`)
        .join(" ")}`,
    );
  }
  if (Object.keys(diagnostics.suspectFaceOwnerCounts).length > 0) {
    lines.push(
      `suspectFaceOwners: ${Object.entries(diagnostics.suspectFaceOwnerCounts)
        .map(([owner, count]) => `${owner}=${count}`)
        .join(" ")}`,
    );
  }
  if (Object.keys(diagnostics.flippedPairSourceCounts).length > 0) {
    lines.push(
      `flippedPairSources: ${Object.entries(diagnostics.flippedPairSourceCounts)
        .map(([source, count]) => `${source}=${count}`)
        .join(" ")}`,
    );
  }
  if (Object.keys(diagnostics.flippedPairOwnerCounts).length > 0) {
    lines.push(
      `flippedPairOwners: ${Object.entries(diagnostics.flippedPairOwnerCounts)
        .map(([owner, count]) => `${owner}=${count}`)
        .join(" ")}`,
    );
  }
  lines.push("");

  if (diagnostics.suspectFaces.length > 0) {
    lines.push(`--- Suspect Faces (${diagnostics.suspectFaces.length}) ---`);
    for (const face of diagnostics.suspectFaces) {
      const fold =
        face.foldAngleDegrees == null
          ? "n/a"
          : face.foldAngleDegrees.toFixed(3);
      const tags = [face.issue, face.source, face.owner]
        .filter(Boolean)
        .join(" ");
      lines.push(
        `f${face.faceIndex} [${tags}]: verts=${face.vertices.join(" ")} foldDeg=${fold} area=${face.area.toFixed(6)}`,
      );
    }
    lines.push("");
  }

  if (diagnostics.flippedAdjacentPairs.length > 0) {
    lines.push(
      `--- Flipped Adjacent Pairs (${diagnostics.flippedAdjacentPairs.length}) ---`,
    );
    for (const pair of diagnostics.flippedAdjacentPairs) {
      const tags = [pair.source, pair.owner].filter(Boolean).join(" ");
      lines.push(
        `e${pair.edgeIndex}${tags ? ` [${tags}]` : ""}: edge=${pair.edge[0]} ${pair.edge[1]} faces=${pair.faces[0]} ${pair.faces[1]} dot=${pair.faceDot.toFixed(6)} angleDeg=${pair.faceAngleDegrees.toFixed(3)}`,
      );
    }
    lines.push("");
  }
}

export function buildBevelDebugReport(
  object: SceneObject,
  baseModifiers: readonly BlendModifier[],
  overrides: readonly ModifierOverride[],
  sceneObjects: ReadonlyMap<string, SceneObject>,
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>,
): string | null {
  const merged = mergeModifierStack(baseModifiers, overrides);
  const bevelCount = merged.filter(
    (modifier) => modifier.kind === "bevel",
  ).length;
  if (bevelCount === 0) return null;

  const sceneObjectsByName = buildSceneObjectNameMap(sceneObjects);
  const resolved = resolveModifierEvaluation(
    {
      position: object.position,
      rotation: object.rotation,
      scale: object.scale,
      vertexGroups: object.vertexGroups,
    },
    merged,
    sceneObjectsByName,
    modifierOverrides,
  );

  const lines: string[] = [];
  lines.push("=== Bevel Debug Export ===");
  lines.push(`name: ${object.name}`);
  lines.push(`id: ${object.id}`);
  lines.push("vertexSpace: local_object");
  lines.push(`objectPosition: [${object.position.join(", ")}]`);
  lines.push(
    `objectRotation: [${object.rotation.map((value) => value.toFixed(6)).join(", ")}]`,
  );
  lines.push(`objectScale: [${object.scale.join(", ")}]`);
  lines.push(`modifierCount: ${resolved.modifiers.length}`);
  lines.push(`bevelModifierCount: ${bevelCount}`);
  lines.push("");
  lines.push("--- Modifier Stack ---");
  resolved.modifiers.forEach((modifier, index) => {
    lines.push(modifierLabel(modifier, index));
    if (modifier.kind === "bevel") {
      lines.push(`  ${bevelSettingsLine(modifier)}`);
    }
  });
  lines.push("");

  let currentGeo = buildSceneObjectModifierGeometry(object);
  let bevelStep = 0;

  for (const [index, modifier] of resolved.modifiers.entries()) {
    if (!modifier.enabled) continue;

    const nextGeo = applyModifiersCore(currentGeo, [modifier], resolved.input);
    if (modifier.kind === "bevel") {
      bevelStep++;
      lines.push(
        `--- Bevel Step ${bevelStep} (${modifierLabel(modifier, index)}) ---`,
      );
      lines.push(bevelSettingsLine(modifier));
      lines.push("");

      appendGeometrySummary(lines, "Input Geometry", currentGeo);
      if (modifier.segments > 1) {
        const stage1Geo = applyModifiersCore(
          currentGeo,
          [
            {
              ...modifier,
              segments: 1,
            },
          ],
          resolved.input,
        );
        appendGeometrySummary(lines, "Stage1 Geometry (segments=1)", stage1Geo);
        appendCompactMeshDump(
          lines,
          object,
          stage1Geo,
          `Bevel Step ${bevelStep} Stage1 Mesh`,
        );
      }
      appendGeometrySummary(lines, "Output Geometry", nextGeo);
      appendOutputDiagnostics(lines, computeOutputGeometryDiagnostics(nextGeo));

      if (!currentGeo.topology) {
        lines.push("inputTopology: none");
        lines.push("");
      } else {
        const vgAllowed = projectVertexGroupThroughProvenance(
          modifier.vertexGroupIndices,
          currentGeo.provenance,
        );
        const diagnostics = diagnoseBevelEdges(
          currentGeo.positions,
          currentGeo.topology,
          modifier,
          vgAllowed,
        );
        const selected = diagnostics.filter((diag) => diag.selected);
        const interestingRejected = diagnostics.filter(
          (diag) =>
            !diag.selected &&
            diag.reason !== "wire" &&
            diag.reason !== "boundary",
        );
        const touchedFaces = new Set<number>();
        let convexSelected = 0;
        let concaveSelected = 0;
        let unknownSelected = 0;
        for (const diag of selected) {
          for (const faceIndex of diag.faces) touchedFaces.add(faceIndex);
          if (diag.profileKind === "convex") convexSelected++;
          else if (diag.profileKind === "concave") concaveSelected++;
          else unknownSelected++;
        }

        lines.push(`selectedEdgeCount: ${selected.length}`);
        lines.push(
          `selectedProfileKinds: convex=${convexSelected} concave=${concaveSelected} unknown=${unknownSelected}`,
        );
        lines.push(`rejectedInteriorEdges: ${interestingRejected.length}`);
        lines.push("");

        if (selected.length > 0) {
          lines.push(`--- Selected Edge Diagnostics (${selected.length}) ---`);
          for (const diag of selected) {
            lines.push(edgeDiagnosticLine(diag));
          }
          lines.push("");
        }

        if (interestingRejected.length > 0) {
          lines.push(
            `--- Rejected Edge Diagnostics (${interestingRejected.length}) ---`,
          );
          for (const diag of interestingRejected) {
            lines.push(edgeDiagnosticLine(diag));
          }
          lines.push("");
        }

        if (touchedFaces.size > 0) {
          lines.push(`--- Touched Face Normals (${touchedFaces.size}) ---`);
          for (const faceIndex of [...touchedFaces].sort((a, b) => a - b)) {
            const [nx, ny, nz] = accumulateFaceNormal(
              currentGeo.topology,
              currentGeo.positions,
              faceIndex,
            );
            lines.push(`f${faceIndex}: ${fmt(nx)} ${fmt(ny)} ${fmt(nz)}`);
          }
          lines.push("");
        }

        appendCompactMeshDump(
          lines,
          object,
          currentGeo,
          `Bevel Step ${bevelStep} Input Mesh`,
        );
        appendCompactMeshDump(
          lines,
          object,
          nextGeo,
          `Bevel Step ${bevelStep} Output Mesh`,
        );
      }
    }

    currentGeo = nextGeo;
  }

  return lines.join("\n");
}

export function collectBevelDebugSnapshot(
  object: SceneObject,
  baseModifiers: readonly BlendModifier[],
  overrides: readonly ModifierOverride[],
  sceneObjects: ReadonlyMap<string, SceneObject>,
  modifierOverrides: ReadonlyMap<string, readonly ModifierOverride[]>,
): BevelDebugSnapshot | null {
  const merged = mergeModifierStack(baseModifiers, overrides);
  const bevelCount = merged.filter(
    (modifier) => modifier.kind === "bevel",
  ).length;
  if (bevelCount === 0) return null;

  const sceneObjectsByName = buildSceneObjectNameMap(sceneObjects);
  const resolved = resolveModifierEvaluation(
    {
      position: object.position,
      rotation: object.rotation,
      scale: object.scale,
      vertexGroups: object.vertexGroups,
    },
    merged,
    sceneObjectsByName,
    modifierOverrides,
  );

  const steps: BevelStepSnapshot[] = [];
  let currentGeo = buildSceneObjectModifierGeometry(object);
  let bevelStep = 0;

  for (const [index, modifier] of resolved.modifiers.entries()) {
    if (!modifier.enabled) continue;

    const nextGeo = applyModifiersCore(currentGeo, [modifier], resolved.input);
    if (modifier.kind === "bevel") {
      bevelStep++;
      const selectionSummary = currentGeo.topology
        ? summarizeBevelSelection(
            diagnoseBevelEdges(
              currentGeo.positions,
              currentGeo.topology,
              modifier,
              projectVertexGroupThroughProvenance(
                modifier.vertexGroupIndices,
                currentGeo.provenance,
              ),
            ),
          )
        : null;
      const stage1Summary =
        modifier.segments > 1
          ? summarizeGeometry(
              applyModifiersCore(
                currentGeo,
                [
                  {
                    ...modifier,
                    segments: 1,
                  },
                ],
                resolved.input,
              ),
            )
          : null;
      const outputDiagnostics = computeOutputGeometryDiagnostics(nextGeo);

      steps.push({
        stepNumber: bevelStep,
        modifierIndex: index,
        modifierLabel: modifierLabel(modifier, index),
        settingsLine: bevelSettingsLine(modifier),
        inputSummary: summarizeGeometry(currentGeo),
        outputSummary: summarizeGeometry(nextGeo),
        stage1Summary,
        selectionSummary,
        outputDiagnostics,
      });
    }

    currentGeo = nextGeo;
  }

  return {
    objectId: object.id,
    objectName: object.name,
    modifierCount: resolved.modifiers.length,
    bevelModifierCount: bevelCount,
    steps,
  };
}
